// src/prototype/hooks/useCoverage.ts
// ============================================================
// THE COVERAGE READ. One hook, one reducer call, every surface.
//
// Reads the uploaded-documents store, hands whatever evidence those uploads carry to
// computeCoverage, and returns the result. It holds no numbers of its own and stores
// nothing: coverage is derived on every render from state that already exists, so there is
// no second copy to fall out of step with the first.
//
// This is the pattern useDemoCompletion established (one shared store, every surface reads
// it) with the stored total removed. useDemoCompletion caches a completion vector; this
// caches nothing, because everything it would cache is derivable from the uploads.
// ============================================================

import { useMemo } from 'react';
import { buildCoverageInputs } from '../../lib/coverage/buildCoverageInputs';
import { computeCoverage, type CoverageResult } from '../../lib/coverage/computeCoverage';
import { useDemoUploads } from './useDemoUploads';

export function useCoverage(): CoverageResult {
  const { evidenceSources } = useDemoUploads();

  // Keyed on the evidence identity, so a re-render that changed nothing does not recompute.
  const key = evidenceSources.map((s) => s.id).join('|');

  return useMemo(
    () => computeCoverage(buildCoverageInputs(evidenceSources)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key],
  );
}

export default useCoverage;
